import { ReportGroupModes } from '../types';
import { ReportItemContent } from './entities';
import { customCompare } from './sorter';

export type SorterFunction = (
  items: ReportItemContent[]
) => ReportItemContent[];

function compareNumbers(a: number | undefined, b: number | undefined): number {
  if (a === undefined && b === undefined) return 0;
  if (a === undefined) return 1;
  if (b === undefined) return -1;
  return a - b;
}

const compareStrings = (
  a: string | undefined,
  b: string | undefined
): number => {
  return customCompare(a || '', b || '');
};


// Helper function to get full element path for hierarchical sorting
const getElementPathForSorting = (item: ReportItemContent): string[] => {
  if (!item.testCase?.element?.path || item.testCase?.element.path.length === 0) {
    return [item.testCase?.element_name || ''];
  }
  return item.testCase?.element.path.map(p => p.name);
};

// Helper function to get test suite title from various sources
const getTestSuiteTitle = (item: ReportItemContent): string => {
  const testSpec = item.testCase?.testSpecification as any;
  return testSpec?.test_suite_title || testSpec?.testSuiteTitle || '';
};

function compareTestId(a: ReportItemContent, b: ReportItemContent): number {
  const testIdCompare = compareNumbers(a.testCase?.assessmentTestId, b.testCase?.assessmentTestId);
  if (testIdCompare !== 0) return testIdCompare;
  return compareStrings(a.testCase?.formId, b.testCase?.formId);
}

function sortByElement(items: ReportItemContent[]): ReportItemContent[] {

  const getGroup = (item: ReportItemContent): number => {
    const isElementActive = Boolean(item.testCase?.fieldSettings?.element?.active);
    // Only consider the structured element object with a non-empty path
    const hasElement = Boolean(item.testCase?.element?.path && item.testCase?.element.path.length > 0);
    if (isElementActive && hasElement) return 0;
    if (isElementActive && !hasElement) return 1;
    return 2;
  };


  return [...items].sort((a, b) => {
    const groupA = getGroup(a);
    const groupB = getGroup(b);
    if (groupA !== groupB) return groupA - groupB;

    const pathA = getElementPathForSorting(a);
    const pathB = getElementPathForSorting(b);

    // Compare each level of the element path hierarchically
    const maxDepth = Math.max(pathA.length, pathB.length);

    for (let i = 0; i < maxDepth; i++) {
      const levelA = pathA[i] || '';
      const levelB = pathB[i] || '';

      const levelCompare = customCompare(levelA, levelB);
      if (levelCompare !== 0) return levelCompare;
    }

    // If paths are identical, sort by path depth (shorter paths first)
    const depthCompare = pathA.length - pathB.length;
    if (depthCompare !== 0) return depthCompare;

    return compareTestId(a, b);
  });
};

function sortByLocation(items: ReportItemContent[]): ReportItemContent[] {
  const getGroup = (item: ReportItemContent): number => {
    const isLocationActive = Boolean(item.testCase?.fieldSettings?.location?.active);
    const hasLocation = Boolean(item.testCase?.buildingId || item.testCase?.levelId || item.testCase?.areaId);
    if (isLocationActive && hasLocation) return 0;
    if (isLocationActive && !hasLocation) return 1;
    return 2;
  };

  return [...items].sort((a, b) => {
    const groupA = getGroup(a);
    const groupB = getGroup(b);
    if (groupA !== groupB) return groupA - groupB;

    // Within a group, keep deterministic ordering. For group 0 (has location),
    // use full location-aware sort. For others, this gracefully falls back
    // because missing fields are empty strings.
    const assetCompare = compareStrings(a.testCase?.asset_name, b.testCase?.asset_name);
    if (assetCompare !== 0) return assetCompare;

    const buildingCompare = compareStrings(a.testCase?.building_description, b.testCase?.building_description);
    if (buildingCompare !== 0) return buildingCompare;

    const levelCompare = compareStrings(a.testCase?.level_description, b.testCase?.level_description);
    if (levelCompare !== 0) return levelCompare;

    const areaCompare = compareStrings(a.testCase?.area_description, b.testCase?.area_description);
    if (areaCompare !== 0) return areaCompare;

    return compareTestId(a, b);
  });
};



function sortByTest(items: ReportItemContent[]): ReportItemContent[] {
  return [...items].sort((a, b) => {
    // Primary: Test Suite Type (compliance, condition, generic)
    const typeA = a.testCase?.testSpecification?.type || '';
    const typeB = b.testCase?.testSpecification?.type || '';
    const typeCompare = customCompare(typeA, typeB);
    if (typeCompare !== 0) return typeCompare;

    // Secondary: Test Suite Title
    const titleA = getTestSuiteTitle(a);
    const titleB = getTestSuiteTitle(b);
    const titleCompare = customCompare(titleA, titleB);
    if (titleCompare !== 0) return titleCompare;

    // Tertiary: Category
    const categoryA = a.testCase?.testSpecification?.category || '';
    const categoryB = b.testCase?.testSpecification?.category || '';
    const categoryCompare = customCompare(categoryA, categoryB);
    if (categoryCompare !== 0) return categoryCompare;

    // Quaternary: SubCategory
    const subcategoryA = a.testCase?.testSpecification?.subcategory || '';
    const subcategoryB = b.testCase?.testSpecification?.subcategory || '';
    const subcategoryCompare = customCompare(subcategoryA, subcategoryB);
    if (subcategoryCompare !== 0) return subcategoryCompare;

    // Quinary: Label
    const labelA = a.testCase?.testSpecification?.label || '';
    const labelB = b.testCase?.testSpecification?.label || '';
    const labelCompare = customCompare(labelA, labelB);
    if (labelCompare !== 0) return labelCompare;

    return compareTestId(a, b);
  });
}

function sortByCategory(items: ReportItemContent[]): ReportItemContent[] {
  return [...items].sort((a, b) => {
    const categoryA = a.testCase?.testSpecification?.category || '';
    const categoryB = b.testCase?.testSpecification?.category || '';
    const categoryCompare = customCompare(categoryA, categoryB);
    if (categoryCompare !== 0) return categoryCompare;

    const labelA = a.testCase?.testSpecification?.label || '';
    const labelB = b.testCase?.testSpecification?.label || '';
    const labelCompare = customCompare(labelA, labelB);
    if (labelCompare !== 0) return labelCompare;

    return compareTestId(a, b);
  });
}

export const sortContentBy = (
  items: ReportItemContent[],
  groupMode: ReportGroupModes
): ReportItemContent[] => {
  if (!items || !groupMode) {
    return items;
  }

  console.debug('sorting content', groupMode);

  switch (groupMode) {
    case 'location':
      return sortByLocation(items);
    case 'element':
      return sortByElement(items);
    case 'test':
      return sortByTest(items);
    case 'category':
      return sortByCategory(items);
    default:
      return sortByLocation(items);
  }
};

export function determineSortOrder(
  groupMode: string,
  headerMode: string
): ReportGroupModes {
  const sortOrder1 = groupMode === 'location' ? 'element' : 'location';
  const sortOrder2 =
    groupMode === 'location' || groupMode === 'element' ? 'test' : 'element';
  return headerMode === 'switch' ? sortOrder2 : sortOrder1 ;
}


export default sortContentBy;
